import type { BillingCycle } from "../types/subscription";
import { canonicalBasicName } from "./categories";
import { CURRENCY_CODES } from "./currencies";

export const USER_SETTINGS_STORAGE_KEY = "dripwatch_user_settings_v1";

/** Fired with `detail` when cloud sync or logout resets settings (see SettingsProvider). */
export const USER_SETTINGS_CLOUD_APPLIED_EVENT = "dripwatch-user-settings-cloud-applied";

function dispatchUserSettingsApplied(settings: UserSettingsState): void {
  try {
    window.dispatchEvent(
      new CustomEvent<UserSettingsState>(USER_SETTINGS_CLOUD_APPLIED_EVENT, {
        detail: settings,
      }),
    );
  } catch {
    /* SSR / restricted environments */
  }
}

export type ThemePreference = "light" | "dark" | "system";

export type UserSettingsState = {
  theme: ThemePreference;
  defaultCurrency: string;
  /** BCP 47 tag, or empty string for browser default */
  numberLocale: string;
  defaultBillingCycle: BillingCycle;
  defaultCategory: string;
  /** User-picked and typed categories to offer again in pickers */
  savedCategoryNames: string[];
  /** Built-in names hidden from quick suggestions */
  dismissedBasicCategories: string[];
  subscriptionListShowInactive: boolean;
  confirmBeforeDelete: boolean;
  dashboardDefaultRenewalWindow: 30 | 60 | 90;
};

export const DEFAULT_USER_SETTINGS: UserSettingsState = {
  theme: "system",
  defaultCurrency: "USD",
  numberLocale: "",
  defaultBillingCycle: "monthly",
  defaultCategory: "Software",
  savedCategoryNames: [],
  dismissedBasicCategories: [],
  subscriptionListShowInactive: true,
  confirmBeforeDelete: true,
  dashboardDefaultRenewalWindow: 30,
};

export function normalizeDefaultCurrency(code: string): string {
  const u = code.trim().toUpperCase();
  return CURRENCY_CODES.has(u) ? u : DEFAULT_USER_SETTINGS.defaultCurrency;
}

function isThemePreference(v: unknown): v is ThemePreference {
  return v === "light" || v === "dark" || v === "system";
}

function isBillingCycle(v: unknown): v is BillingCycle {
  return v === "monthly" || v === "yearly" || v === "custom_days";
}

function isRenewalWindow(v: unknown): v is 30 | 60 | 90 {
  return v === 30 || v === 60 || v === 90;
}

function sanitizeStringList(v: unknown, maxLen: number): string[] {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const item of v) {
    if (typeof item !== "string") continue;
    const s = item.trim().slice(0, maxLen);
    if (!s) continue;
    const key = s.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(s);
    if (out.length >= 80) break;
  }
  return out;
}

/** Normalize API or local JSON into a full settings object. */
export function coerceUserSettings(
  parsed: unknown,
): UserSettingsState {
  if (typeof parsed !== "object" || parsed === null) {
    return { ...DEFAULT_USER_SETTINGS };
  }
  const p = parsed as Partial<UserSettingsState>;
  return {
    theme: isThemePreference(p.theme) ? p.theme : DEFAULT_USER_SETTINGS.theme,
    defaultCurrency: normalizeDefaultCurrency(
      typeof p.defaultCurrency === "string"
        ? p.defaultCurrency
        : DEFAULT_USER_SETTINGS.defaultCurrency,
    ),
    numberLocale:
      typeof p.numberLocale === "string"
        ? p.numberLocale
        : DEFAULT_USER_SETTINGS.numberLocale,
    defaultBillingCycle: isBillingCycle(p.defaultBillingCycle)
      ? p.defaultBillingCycle
      : DEFAULT_USER_SETTINGS.defaultBillingCycle,
    defaultCategory:
      typeof p.defaultCategory === "string" && p.defaultCategory.trim()
        ? p.defaultCategory.trim()
        : DEFAULT_USER_SETTINGS.defaultCategory,
    savedCategoryNames: sanitizeStringList(p.savedCategoryNames, 200),
    dismissedBasicCategories: sanitizeStringList(p.dismissedBasicCategories, 200)
      .map((c) => canonicalBasicName(c))
      .filter((c): c is NonNullable<typeof c> => c !== null),
    subscriptionListShowInactive:
      typeof p.subscriptionListShowInactive === "boolean"
        ? p.subscriptionListShowInactive
        : DEFAULT_USER_SETTINGS.subscriptionListShowInactive,
    confirmBeforeDelete:
      typeof p.confirmBeforeDelete === "boolean"
        ? p.confirmBeforeDelete
        : DEFAULT_USER_SETTINGS.confirmBeforeDelete,
    dashboardDefaultRenewalWindow: isRenewalWindow(
      p.dashboardDefaultRenewalWindow,
    )
      ? p.dashboardDefaultRenewalWindow
      : DEFAULT_USER_SETTINGS.dashboardDefaultRenewalWindow,
  };
}

export function loadUserSettings(): UserSettingsState {
  try {
    const raw = localStorage.getItem(USER_SETTINGS_STORAGE_KEY);
    if (!raw) return { ...DEFAULT_USER_SETTINGS };
    return coerceUserSettings(JSON.parse(raw) as unknown);
  } catch {
    return { ...DEFAULT_USER_SETTINGS };
  }
}

export function saveUserSettings(settings: UserSettingsState): void {
  try {
    localStorage.setItem(USER_SETTINGS_STORAGE_KEY, JSON.stringify(settings));
  } catch {
    /* ignore quota / private mode */
  }
}

/** Persist and notify React context (cloud pull). */
export function applyUserSettingsFromCloud(settings: UserSettingsState): void {
  saveUserSettings(settings);
  dispatchUserSettingsApplied(settings);
}

export function clearUserSettingsLocalCache(): void {
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.removeItem(USER_SETTINGS_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
  dispatchUserSettingsApplied({ ...DEFAULT_USER_SETTINGS });
}

/** Apply theme class and color-scheme on `document.documentElement`. */
export function applyThemePreference(pref: ThemePreference): void {
  const root = document.documentElement;
  root.classList.remove("light", "dark");
  let resolved: "light" | "dark";
  if (pref === "system") {
    resolved = window.matchMedia("(prefers-color-scheme: dark)").matches
      ? "dark"
      : "light";
  } else {
    resolved = pref;
  }
  root.classList.add(resolved);
  root.style.colorScheme = resolved;
}
